import { Link, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, CheckCircle2, HelpCircle, Phone } from "lucide-react";

// This would typically come from an API or a data file
const servicesData: Record<string, {
  title: string;
  description: string;
  longDescription: string | string[];
  benefits: string[];
  image: string;
  imageAlt?: string;
  faq: { q: string; a: string }[];
}> = {
  ossigenoterapia: {
    title: "Ossigenoterapia Domiciliare",
    description: "Cos’è, quando serve e quali benefici offre l’ossigenoterapia a casa.",
    longDescription: [
      "L'ossigenoterapia è un trattamento medico che fornisce ai polmoni una concentrazione di ossigeno superiore a quella presente nell'aria (che è del 21%). L'obiettivo è semplice ma vitale: assicurare che il sangue trasporti la giusta quantità di ossigeno a cellule, tessuti e organi, migliorando le funzioni corporee e riducendo l'affanno.",
      "Il trattamento viene erogato tramite dispositivi specifici, prescritti dal medico in base alle esigenze del paziente:\n\n• Cannule nasali: sottili e comodi tubicini da inserire nelle narici, ideali per una terapia a lungo termine.\n• Maschere facciali: dispositivi che coprono naso e bocca per erogare concentrazioni di ossigeno più elevate.\n• Concentratori di ossigeno: macchinari che estraggono ossigeno dall'aria circostante, eliminando la necessità di bombole.\n• Bombole di ossigeno liquido o gassoso: soluzioni per garantire mobilità o un'alta erogazione.",
      "Quando è necessaria l'ossigenoterapia domiciliare? Un medico può prescriverla per diverse condizioni croniche o acute che causano ipossiemia (bassi livelli di ossigeno nel sangue). Le più comuni includono:\n\n• Malattie polmonari croniche: come la BPCO (Broncopneumopatia Cronica Ostruttiva), enfisema e fibrosi polmonare.\n• Insufficienza cardiaca: quando il cuore non pompa sangue in modo efficiente.\n• Apnee notturne severe.\n• Fasi di convalescenza post-chirurgica o dopo traumi polmonari.\n• Altre condizioni che limitano la funzionalità dei polmoni.",
      "I benefici concreti dell’ossigenoterapia a casa:\n\n• Migliore respirazione e riduzione della sensazione di “fame d’aria”.\n• Aumento dei livelli di energia e diminuzione della stanchezza cronica.\n• Migliore qualità del sonno e maggiore lucidità mentale durante il giorno.\n• Maggiore autonomia nelle attività quotidiane.\n• Prevenzione di complicanze legate a bassi livelli di ossigeno.\n\nIn sintesi: una qualità della vita notevolmente superiore.",
      "Gestire l’ossigenoterapia in sicurezza: sebbene sia un trattamento sicuro, è fondamentale seguire la prescrizione medica e le corrette norme d’uso. Effetti collaterali lievi come secchezza nasale o mal di testa sono gestibili. Il nostro personale qualificato fornisce ai pazienti in Puglia la formazione necessaria per utilizzare i dispositivi in totale sicurezza.",
      "Puglia Life offre un servizio completo di fornitura e assistenza per tutte queste soluzioni di ossigenoterapia a domicilio su tutto il territorio pugliese. Personale qualificato, consegna puntuale in tutte le province (Bari, Brindisi, Foggia, Lecce, Taranto, BAT), assistenza clienti dedicata e monitoraggio costante: respira meglio, vivi meglio. Inizia oggi il tuo percorso con Puglia Life."
    ],
    benefits: [
      "Riduzione dell’affanno e migliore ossigenazione del sangue",
      "Più energia e meno stanchezza cronica",
      "Sonno migliore e maggiore lucidità durante il giorno",
      "Maggiore autonomia nelle attività quotidiane",
      "Concentratori, cannule, maschere e bombole su prescrizione medica",
      "Consegna a domicilio puntuale in tutta la Puglia",
      "Formazione all’uso sicuro dei dispositivi",
      "Assistenza clienti dedicata e supporto continuativo"
    ],
    image: "/images/services/Ossigeno_Terapia.png",
    imageAlt: "Paziente che utilizza correttamente una mascherina di ossigeno in ambiente clinico",
    faq: [
      {
        q: "Quando è necessaria l’ossigenoterapia domiciliare?",
        a: "È indicata quando il medico rileva ipossiemia (bassi livelli di ossigeno nel sangue) e può essere prescritta per condizioni come BPCO, enfisema, fibrosi polmonare, insufficienza cardiaca, apnee notturne severe o convalescenze post-chirurgiche."
      },
      {
        q: "Quali dispositivi si usano per l’ossigenoterapia?",
        a: "In base alla prescrizione medica si possono usare cannule nasali, maschere facciali, concentratori di ossigeno oppure bombole di ossigeno liquido o gassoso."
      },
      {
        q: "Quali benefici posso aspettarmi a casa?",
        a: "In genere si osservano riduzione dell’affanno, più energia, migliore qualità del sonno, maggiore lucidità e autonomia, con prevenzione di complicanze legate a bassi livelli di ossigeno."
      },
      {
        q: "È un trattamento sicuro?",
        a: "Sì, se si seguono prescrizione medica e norme d’uso. Eventuali effetti lievi (secchezza nasale, mal di testa) sono gestibili e ti formiamo all’utilizzo corretto del dispositivo."
      },
      {
        q: "Coprite tutta la Puglia con consegna e assistenza?",
        a: "Sì: organizziamo fornitura e assistenza in tutte le province della Puglia (Bari, Brindisi, Foggia, Lecce, Taranto, BAT), con supporto dedicato durante il percorso."
      }
    ]
  },
  ventiloterapia: {
    title: "Ventiloterapia Domiciliare",
    description: "Supporto respiratorio quando il corpo non può respirare da solo.",
    longDescription: [
      "Respirare è vita. Quando una patologia impedisce ai polmoni di svolgere il loro lavoro in modo efficace, la ventiloterapia (o ventilazione meccanica) interviene come un supporto vitale. Oggi, grazie alle tecnologie moderne, la ventiloterapia può essere gestita anche a domicilio, permettendo a molti pazienti in Puglia di continuare le cure nella serenità della propria casa.",
      "Cos’è la ventiloterapia? È un trattamento medico che assiste la respirazione attraverso un ventilatore meccanico (spesso chiamato “respiratore”). Il dispositivo spinge una miscela di aria e/o ossigeno nei polmoni, aiutando il paziente a compiere l’atto respiratorio quando non riesce a farlo in modo autonomo o efficace.",
      "La differenza fondamentale: ventiloterapia invasiva e non invasiva (NIV).\n\n• Ventiloterapia invasiva (VMI): utilizzata nelle situazioni più critiche, richiede l’inserimento di un tubo nella trachea tramite intubazione o tracheotomia. È una procedura gestita in ambienti controllati (es. terapie intensive).\n• Ventiloterapia non invasiva (NIV): è la soluzione più comune in ambito domiciliare. Il supporto respiratorio viene fornito tramite maschere nasali, facciali o casco collegati al ventilatore. Quando clinicamente appropriata, è più confortevole e consente di preservare la capacità di parlare e deglutire.",
      "Quando è indicata la ventiloterapia domiciliare? La prescrive il medico specialista per pazienti con insufficienza respiratoria cronica, spesso legata a:\n\n• Malattie polmonari croniche (BPCO grave, enfisema, fibrosi polmonare).\n• Malattie neuromuscolari (es. SLA o distrofie muscolari) che indeboliscono i muscoli respiratori.\n• Deformità della gabbia toracica.\n• Sindrome da ipoventilazione-obesità.",
      "Benefici e gestione dei rischi a domicilio: se gestita da personale esperto, la ventiloterapia domiciliare può migliorare l’ossigenazione, ridurre l’accumulo di anidride carbonica, diminuire la fatica respiratoria e migliorare la qualità del sonno. Inoltre può ridurre ricoveri e complicanze. I rischi (es. irritazioni cutanee da maschera o infezioni) si minimizzano con formazione, accessori adeguati e monitoraggio costante.",
      "Puglia Life è al tuo fianco in tutta la Puglia con un servizio completo e umano: fornitura di ventilatori all’avanguardia, installazione e settaggio, addestramento di paziente e caregiver, assistenza continua e controlli periodici, oltre alla fornitura di maschere e circuiti per una terapia efficace e confortevole."
    ],
    benefits: [
      "Migliore ossigenazione e riduzione dell’anidride carbonica",
      "Meno fatica respiratoria e migliore qualità del sonno",
      "Riduzione dei ricoveri e prevenzione di complicanze",
      "Dispositivi moderni e silenziosi, personalizzati sul paziente",
      "Maschere e accessori per comfort ed efficacia della terapia",
      "Formazione di paziente e caregiver, con monitoraggio costante",
      "Assistenza continua in tutte le province pugliesi"
    ],
    image: "/images/services/ventiloterapia.jpg",
    imageAlt: "Paziente che utilizza ventilazione non invasiva con maschera e ventilatore a domicilio",
    faq: [
      {
        q: "Che cos’è la ventiloterapia?",
        a: "È un trattamento che assiste la respirazione tramite un ventilatore meccanico che spinge aria e/o ossigeno nei polmoni quando la respirazione spontanea non è sufficiente."
      },
      {
        q: "Qual è la differenza tra ventiloterapia invasiva e non invasiva?",
        a: "La ventiloterapia invasiva richiede intubazione o tracheotomia ed è tipica dell’ospedale. La non invasiva (NIV) usa maschere o casco ed è spesso gestibile a domicilio, quando indicato dallo specialista."
      },
      {
        q: "Quando è indicata la ventiloterapia domiciliare?",
        a: "Può essere prescritta in caso di insufficienza respiratoria cronica legata a BPCO grave, enfisema, fibrosi polmonare, malattie neuromuscolari, deformità toraciche o sindrome da ipoventilazione-obesità."
      },
      {
        q: "Quali benefici offre a casa?",
        a: "Può migliorare l’ossigenazione, ridurre la CO₂, diminuire la fatica respiratoria e migliorare il sonno, riducendo ricoveri e complicanze se ben gestita e monitorata."
      },
      {
        q: "Come gestite comfort e rischi della maschera?",
        a: "Selezioniamo maschere e accessori adeguati, formiamo paziente e caregiver e facciamo monitoraggio e controlli periodici per ridurre irritazioni cutanee e altri inconvenienti."
      }
    ]
  },
  nutrizione: {
    title: "Nutrizione Artificiale Domiciliare",
    description: "Un percorso nutrizionale sicuro per nutrirsi nel comfort di casa.",
    longDescription: [
      "La Nutrizione Artificiale Domiciliare (NAD) è un supporto terapeutico pensato per i pazienti che, a causa di patologie o condizioni cliniche, non riescono ad assumere con l’alimentazione orale una quantità sufficiente di nutrienti. L’obiettivo è garantire l’apporto adeguato di energia, proteine e micronutrienti, migliorando lo stato di salute e la qualità della vita.",
      "Come funziona la Nutrizione Artificiale Domiciliare? La NAD può essere somministrata in due modalità, valutate dallo specialista in base alle esigenze del paziente:\n\n• Nutrizione enterale (NE): i nutrienti vengono introdotti nel tratto gastrointestinale tramite sondino nasogastrico o gastrostomia.\n• Nutrizione parenterale (NP): i nutrienti vengono somministrati per via endovenosa tramite un catetere in vena centrale.",
      "Quali sono i vantaggi della NAD?\n\n• Migliore qualità della vita: permette di rimanere a casa, riducendo l’impatto psicologico del ricovero.\n• Minore rischio di infezioni: riduce l’esposizione alle infezioni ospedaliere.\n• Maggiore autonomia: aiuta a gestire la terapia con più serenità.\n• Riduzione dei costi: nel lungo periodo può ridurre costi legati a ricoveri ripetuti.",
      "A chi è rivolta la Nutrizione Artificiale Domiciliare?\n\n• Pazienti che non riescono ad assumere nutrienti sufficienti con la normale alimentazione.\n• Pazienti con malassorbimento intestinale.\n• Persone che hanno subito interventi chirurgici che rendono difficile o impossibile l’alimentazione orale.\n• Pazienti con patologie neurologiche o neuromuscolari che compromettono la deglutizione.\n• Pazienti con traumi cranici o facciali.",
      "Come si avvia la NAD? Il percorso viene definito in follow-up con un medico specialista in nutrizione clinica e in genere prevede:\n\n• Valutazione nutrizionale: analisi dello stato nutrizionale e del fabbisogno individuale.\n• Scelta del tipo di accesso: definizione della modalità (NE o NP) e, se necessario, pianificazione del posizionamento di sondino/catetere.\n• Addestramento di paziente e caregiver: istruzioni su preparazione, somministrazione e cura del sito di accesso.\n• Monitoraggio: controlli periodici per verificare l’andamento della terapia e ottimizzare il piano nutrizionale.",
      "Puglia Life offre un servizio completo e personalizzato per la Nutrizione Artificiale Domiciliare su tutto il territorio pugliese: consulenza specialistica, fornitura di materiali e presidi, addestramento, assistenza infermieristica domiciliare e monitoraggio continuo. Contattaci per maggiori informazioni: insieme possiamo prenderci cura della tua salute e garantirti una vita migliore."
    ],
    benefits: [
      "Percorso nutrizionale personalizzato con specialisti",
      "Gestione a domicilio nel comfort di casa",
      "Riduzione dell’esposizione alle infezioni ospedaliere",
      "Addestramento di paziente e caregiver",
      "Fornitura di presidi e materiali necessari",
      "Assistenza infermieristica e monitoraggio continuo"
    ],
    image: "/images/services/nutrizione.jpg",
    imageAlt: "Caregiver che prepara nutrizione enterale a domicilio con pompa e sacca",
    faq: [
      {
        q: "Qual è la differenza tra nutrizione enterale e parenterale?",
        a: "La nutrizione enterale usa il tratto gastrointestinale (sondino o gastrostomia), mentre la nutrizione parenterale somministra i nutrienti per via endovenosa tramite un catetere in vena centrale."
      },
      {
        q: "A chi è indicata la Nutrizione Artificiale Domiciliare?",
        a: "È indicata quando l’alimentazione orale non copre il fabbisogno nutrizionale, ad esempio in caso di malassorbimento, esiti chirurgici, patologie neurologiche con disfagia o traumi."
      },
      {
        q: "Come viene avviata la terapia a domicilio?",
        a: "Dopo valutazione dello specialista in nutrizione clinica si definiscono modalità e accesso, si addestrano paziente e caregiver e si avvia un monitoraggio periodico per adattare il piano nutrizionale."
      },
      {
        q: "Puglia Life fornisce anche assistenza e materiali?",
        a: "Sì: forniamo presidi e materiali, addestramento, assistenza infermieristica domiciliare e monitoraggio continuo durante tutto il percorso."
      }
    ]
  },
  sonno: {
    title: "Disturbi del Sonno",
    description: "Diagnosi e trattamento di apnee notturne, insonnia e altri disturbi del sonno.",
    longDescription: [
      "Durante il riposo notturno il corpo si rigenera, la mente consolida la memoria e il sistema immunitario si rafforza. Un sonno regolare e di qualità è essenziale per il benessere fisico e mentale. Quando questo equilibrio si interrompe, disturbi come insonnia, apnee notturne, narcolessia e sonnambulismo possono compromettere seriamente la qualità della vita, con stanchezza diurna, difficoltà di concentrazione, irritabilità e rischi per la salute.",
      "Per affrontare questi problemi è importante una valutazione specialistica e un percorso mirato. In base al caso, la diagnosi può includere esami come polisonnografia, poligrafia cardiorespiratoria, actigrafia e altri monitoraggi utili a definire natura e gravità del disturbo.",
      "Le terapie vengono personalizzate: possono prevedere percorsi comportamentali (come la terapia cognitivo-comportamentale per l’insonnia), terapia ventilatoria per le apnee notturne (ad esempio con dispositivi a pressione positiva) e, quando indicato, trattamento farmacologico o altre soluzioni specifiche.",
      "Puglia Life ti supporta anche a domicilio nel percorso di terapia ventilatoria per le apnee notturne: selezione del dispositivo e della maschera più adatti, settaggio, addestramento di paziente e caregiver e assistenza continuativa per massimizzare comfort, aderenza e risultati."
    ],
    benefits: [
      "Valutazione dei disturbi del sonno e inquadramento dei sintomi",
      "Diagnostica dedicata (polisonnografia, poligrafia, actigrafia)",
      "Percorsi terapeutici personalizzati in base alla diagnosi",
      "Terapia ventilatoria per apnee notturne con dispositivi e maschere confortevoli",
      "Supporto a domicilio: consegna, settaggio e formazione all’uso",
      "Assistenza continuativa e follow-up per migliorare aderenza e risultati"
    ],
    image: "/images/services/sonno.jpg",
    imageAlt: "Paziente che dorme con maschera CPAP e dispositivo sul comodino",
    faq: [
      {
        q: "Quali disturbi del sonno trattate più spesso?",
        a: "Tra i più comuni ci sono insonnia e apnee notturne, ma la valutazione può includere anche altri disturbi come narcolessia e parasomnie, in base ai sintomi riferiti."
      },
      {
        q: "Quali esami vengono usati per la diagnosi?",
        a: "In base al quadro clinico si possono utilizzare polisonnografia, poligrafia cardiorespiratoria, actigrafia e altri esami mirati per definire natura e gravità del disturbo."
      },
      {
        q: "Che differenza c’è tra polisonnografia e poligrafia?",
        a: "La polisonnografia è un esame più completo che registra più parametri legati al sonno; la poligrafia cardiorespiratoria è spesso focalizzata sugli eventi respiratori notturni. La scelta dipende dall’indicazione specialistica."
      },
      {
        q: "Quando serve la terapia ventilatoria per le apnee notturne?",
        a: "Quando lo specialista riscontra apnee notturne clinicamente significative. In questi casi si possono usare dispositivi a pressione positiva e maschere dedicate, con settaggio personalizzato."
      },
      {
        q: "Come mi supportate a domicilio con CPAP o terapia ventilatoria?",
        a: "Forniamo dispositivo e accessori, aiutiamo nella scelta della maschera, eseguiamo installazione e settaggio, formiamo paziente e caregiver e garantiamo assistenza continuativa e follow-up."
      }
    ]
  },
  // Default fallback for other services
  default: {
    title: "Servizio Home Care",
    description: "Assistenza domiciliare professionale personalizzata.",
    longDescription: "Puglia Life offre una gamma completa di servizi di assistenza domiciliare per migliorare la qualità della vita dei pazienti. Contattaci per maggiori informazioni sui nostri servizi specifici.",
    benefits: [
      "Personale qualificato",
      "Assistenza personalizzata",
      "Reperibilità costante",
      "Tecnologie all'avanguardia"
    ],
    image: "/images/site/home-care.jpg",
    imageAlt: "Infermiere che assiste un paziente anziano a domicilio",
    faq: []
  }
};

export function ServiceDetail() {
  const { slug } = useParams();
  const service = servicesData[slug as string] || servicesData.default;
  const longDescriptionBlocks = Array.isArray(service.longDescription)
    ? service.longDescription
    : [service.longDescription];

  return (
    <div className="pb-16">
      {/* Hero Service */}
      <section className="bg-brand-neutral py-16 md:py-24">
        <div className="container">
          <Link to="/" className="inline-flex items-center text-text-body hover:text-brand-primary mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" /> Torna alla Home
          </Link>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-brand-primary font-bold tracking-wide uppercase text-sm">Servizi Home Care</span>
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-text-title">{service.title}</h1>
              <p className="text-xl text-text-body leading-relaxed">
                {service.description}
              </p>
              <Button size="lg" asChild>
                <Link to="/contatti">Richiedi Informazioni</Link>
              </Button>
            </div>
            <div>
              <img 
                src={service.image} 
                alt={service.imageAlt ?? service.title} 
                className="rounded-2xl shadow-xl w-full object-cover aspect-video"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="container py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            <section>
              <h2 className="text-2xl font-heading font-bold text-text-title mb-4">Descrizione del Servizio</h2>
              <div className="space-y-4">
                {longDescriptionBlocks.map((block, index) => (
                  <p key={index} className="text-text-body text-lg leading-relaxed whitespace-pre-line">
                    {block}
                  </p>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-text-title mb-6">I Vantaggi</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {service.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                    <CheckCircle2 className="h-5 w-5 text-[#6B8E00] shrink-0 mt-0.5" />
                    <span className="text-text-body font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </section>

            {service.faq.length > 0 && (
              <section>
                <h2 className="text-2xl font-heading font-bold text-text-title mb-6">Domande Frequenti</h2>
                <div className="space-y-4">
                  {service.faq.map((item, index) => (
                    <div key={index} className="border rounded-lg p-6 bg-gray-50">
                      <h3 className="flex items-center text-lg font-bold text-text-title mb-2">
                        <HelpCircle className="h-5 w-5 text-brand-primary mr-2" />
                        {item.q}
                      </h3>
                      <p className="text-text-body ml-7">{item.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            <div className="bg-brand-primary text-white p-8 rounded-2xl shadow-lg">
              <Phone className="h-8 w-8 mb-4" />
              <h3 className="text-xl font-bold mb-2">Serve aiuto immediato?</h3>
              <p className="text-blue-100 mb-6">
                Il nostro team è a tua disposizione per fornirti tutte le informazioni necessarie.
              </p>
              <Button variant="secondary" className="w-full" asChild>
                <Link to="/contatti">Contattaci</Link>
              </Button>
            </div>

            <div className="bg-white p-6 rounded-2xl border shadow-sm">
              <h3 className="font-bold text-text-title mb-4">Altri Servizi</h3>
              <nav className="flex flex-col space-y-2">
                <Link to="/servizi/ossigenoterapia" className="text-text-body hover:text-brand-primary hover:bg-gray-50 p-2 rounded-md transition-colors block">
                  Ossigenoterapia
                </Link>
                <Link to="/servizi/ventiloterapia" className="text-text-body hover:text-brand-primary hover:bg-gray-50 p-2 rounded-md transition-colors block">
                  Ventiloterapia
                </Link>
                <Link to="/servizi/sonno" className="text-text-body hover:text-brand-primary hover:bg-gray-50 p-2 rounded-md transition-colors block">
                  Disturbi del Sonno
                </Link>
                <Link to="/servizi/nutrizione" className="text-text-body hover:text-brand-primary hover:bg-gray-50 p-2 rounded-md transition-colors block">
                  Nutrizione Clinica
                </Link>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
